import { EffectRef, WritableSignal } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { MapCoord } from 'src/app/interfaces/api/model';

interface Point {
    name: string,
    lat: number,
    lng: number,
}

interface MarkerInfo {
    name: string;
    marker: google.maps.Marker;
}


export class ImageTransform {
    private static readonly CORNER_MARKER_SVG = "M 35.701 7.632 C 35.316 8.018, 35 14.095, 35 21.138 L 35 33.942 21.750 34.221 C 9.260 34.484, 8.500 34.615, 8.500 36.500 C 8.500 38.414, 9.167 38.500, 24 38.500 L 39.500 38.500 39.500 23.014 C 39.500 9.826, 39.270 7.483, 37.951 7.229 C 37.099 7.065, 36.087 7.246, 35.701 7.632";
    private static readonly CENTER_MARKER_SVG = "M 43.719 12.779 C 37.996 18.562, 36.499 22, 39.702 22 C 40.639 22, 42.889 20.562, 44.702 18.804 L 48 15.608 48 26.804 C 48 37.333, 48.119 38, 50 38 C 51.881 38, 52 37.333, 52 26.804 L 52 15.608 55.298 18.804 C 58.946 22.340, 62 22.984, 62 20.219 C 62 18.685, 51.339 7, 49.940 7 C 49.663 7, 46.864 9.601, 43.719 12.779 M 12.500 44 L 6.563 50 12.500 56 C 18.393 61.956, 22 63.587, 22 60.298 C 22 59.361, 20.562 57.111, 18.804 55.298 L 15.608 52 26.804 52 C 37.333 52, 38 51.881, 38 50 C 38 48.119, 37.333 48, 26.804 48 L 15.608 48 18.804 44.702 C 20.562 42.889, 22 40.639, 22 39.702 C 22 36.413, 18.393 38.044, 12.500 44 M 78 39.702 C 78 40.639, 79.438 42.889, 81.196 44.702 L 84.392 48 73.196 48 C 62.667 48, 62 48.119, 62 50 C 62 51.881, 62.667 52, 73.196 52 L 84.392 52 81.196 55.298 C 79.438 57.111, 78 59.361, 78 60.298 C 78 63.587, 81.607 61.956, 87.500 56 L 93.437 50 87.500 44 C 81.607 38.044, 78 36.413, 78 39.702 M 48 73.196 L 48 84.392 44.702 81.196 C 42.889 79.438, 40.639 78, 39.702 78 C 36.413 78, 38.044 81.607, 44 87.500 L 50 93.437 56 87.500 C 61.956 81.607, 63.587 78, 60.298 78 C 59.361 78, 57.111 79.438, 55.298 81.196 L 52 84.392 52 73.196 C 52 62.667, 51.881 62, 50 62 C 48.119 62, 48 62.667, 48 73.196";
    private static readonly CORNER_ANCHOR = new google.maps.Point(37, 37);
    private static readonly CENTER_ANCHOR = new google.maps.Point(46, 45);
    private static readonly FINAL_DEBOUNCE_DELAY = 50;

    private updateTimeout: ReturnType<typeof setTimeout> | null = null;
    private previewRafId: number | null = null;
    private lastPreviewCoords: string | null = null;
    private dragStartTime: number = 0;
    private isDragging: boolean = false;
    private imageDataEffect: EffectRef | null = null;

    imageCoord!: MapCoord;
    points: Point[] = [];
    markers: MarkerInfo[] = [];
    map!: GoogleMap;
    onUpdate!: (newCoords: MapCoord, resized: boolean, mode?: 'preview' | 'final') => void;
    isEnabled: boolean = false;
    resized: boolean = false;
    imageData!: WritableSignal<any>;
    resizedCoord!: MapCoord;
    tempCoord!: MapCoord;

    constructor(options: {
        map: GoogleMap;
        imageCoord: MapCoord;
        imageData: WritableSignal<any>;
        onUpdate: (newCoords: MapCoord, resized: boolean, mode?: 'preview' | 'final') => void;
    }) {
        this.onUpdate = options.onUpdate;
        this.map = options.map;
        if (!options.imageCoord) {
            console.error("Image hasn't coordinate");
            return;
        }

        this.imageCoord = options.imageCoord;
        this.resizedCoord = {
            north: this.imageCoord.north,
            east: this.imageCoord.east,
            south: this.imageCoord.south,
            west: this.imageCoord.west,
        };
        this.tempCoord = { ...this.resizedCoord };

        this.imageData = options.imageData;
        this.buildPoints();
        this.enable();
    }

    enable() {
        if (this.points.length == 0)
            this.buildPoints();

        this.points.forEach(p => {
            this.addMarker(p);
        })

        this.addCenterMarker();
        this.isEnabled = true;
    }

    disable() {
        this.markers.forEach(m => m.marker.setMap(null))
        this.markers = [];
        this.points = [];
        this.isEnabled = false;
        this.resized = false;
    }

    destroy() {
        this.markers.forEach(m => {
            if (m.marker) {
                google.maps.event.clearInstanceListeners(m.marker);
            }
        });

        this.disable();

        if (this.imageDataEffect) {
            this.imageDataEffect.destroy();
            this.imageDataEffect = null;
        }

        this.map = null as any;
        this.points = [];
        this.markers = [];
        this.imageCoord = null as any;
        this.onUpdate = null as any;

        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = null;
        }

        this.lastPreviewCoords = null;
        this.isDragging = false;

        if (this.previewRafId !== null) {
            cancelAnimationFrame(this.previewRafId);
            this.previewRafId = null;
        }
    }

    reload(imageData: { coord: MapCoord }) {
        this.imageCoord = imageData.coord;
        this.resizedCoord = {
            north: this.imageCoord.north,
            east: this.imageCoord.east,
            south: this.imageCoord.south,
            west: this.imageCoord.west,
        };
        this.tempCoord = { ...this.resizedCoord };
        this.disable();
        this.enable();
    }

    deleteImage() {
        this.markers.forEach(markerObj => {
            markerObj.marker.setMap(null);
        });
        this.markers = [];
        this.points = [];
        if (this.onUpdate) {
            this.onUpdate(this.imageCoord, false);
        }
    }

    refreshMap() {
        const googleMapInstance = this.map.googleMap;

        if (googleMapInstance) {
            const currentZoom = googleMapInstance.getZoom();
            googleMapInstance.setZoom((currentZoom ?? 0) + 1);
            googleMapInstance.setZoom(currentZoom ?? 0);
        }
    }

    buildPoints() {
        this.points = [];

        const coordinates = [
            { name: "N", lat: this.resizedCoord.north, lng: this.resizedCoord.west },
            { name: "E", lat: this.resizedCoord.north, lng: this.resizedCoord.east },
            { name: "S", lat: this.resizedCoord.south, lng: this.resizedCoord.east },
            { name: "W", lat: this.resizedCoord.south, lng: this.resizedCoord.west },
        ];

        this.points.push(...coordinates);
    }
    rotationImage(angleDegrees: number) {
        const canvasContainer = document.createElement('canvas-container');
        const canvas = document.createElement('canvas');
        // this.canvasElement = canvas;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error("Could not get 2D context from canvas.");
        }

        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            let width = image.width;
            let height = image.height;

            const angle = angleDegrees * Math.PI / 180;

            const rotatedWidth = Math.abs(width * Math.cos(angle)) + Math.abs(height * Math.sin(angle));
            const rotatedHeight = Math.abs(width * Math.sin(angle)) + Math.abs(height * Math.cos(angle));

            canvas.width = rotatedWidth;
            canvas.height = rotatedHeight;

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
            ctx.rotate(angle);

            ctx.drawImage(image, -width / 2, -height / 2, width, height);

            canvasContainer.style.width = rotatedWidth + 'px';
            canvasContainer.style.height = rotatedHeight + 'px';

            if (!this.imageData) return;
            this.imageData().mapImage = canvas.toDataURL();
            this.imageData().origin.mapAngle = angleDegrees;

        }

        image.onerror = (error) => {
            console.error('Load image error :>> ', error);
        }

        if (!this.imageData().mapImageSrc) return;
        image.src = this.imageData().mapImageSrc;
    }

    updatePointFromMarker(point: Point, marker: any) {
        const currPos = marker.getPosition();
        const pos = { lat: currPos.lat(), lng: currPos.lng() };

        point.lat = pos.lat;
        point.lng = pos.lng;

        this.updateMarkers(point.name);
    }

    async addMarker(point: Point) {
        let rotation = 0;
        if (point.name == "W") rotation = 90;
        if (point.name == "N") rotation = 180;
        if (point.name == "E") rotation = 270;

        const svgPath = ImageTransform.CORNER_MARKER_SVG;

        const svgMarker = {
            path: svgPath,
            fillColor: "white",
            fillOpacity: 1,
            strokeColor: "white",
            strokeWeight: 1,
            rotation,
            scale: 1,
            anchor: ImageTransform.CORNER_ANCHOR,
        };

        let marker = new google.maps.Marker({
            position: { lat: point.lat, lng: point.lng },
            icon: svgMarker,
            map: this.map.googleMap
        })

        marker.setDraggable(true);

        try {
            google.maps.event.addListener(marker, 'dragstart', () => {
                this.dragStartTime = Date.now();
                this.isDragging = true;
            });

            google.maps.event.addListener(marker, 'drag', () => {
                if (!this.isDragging) return;

                const now = Date.now();
                if (now - this.dragStartTime < 0) return;

                requestAnimationFrame(() => {
                    this.updatePointFromMarker(point, marker);
                    this.debouncedUpdate(this.resizedCoord, this.resized, 'preview');
                });
            });

            google.maps.event.addListener(marker, 'dragend', () => {
                this.isDragging = false;
                this.updatePointFromMarker(point, marker);
                this.resized = true;

                const newCoord = {
                    north: this.resizedCoord.north,
                    east: this.resizedCoord.east,
                    south: this.resizedCoord.south,
                    west: this.resizedCoord.west
                };

                this.debouncedUpdate(newCoord, this.resized, 'final');
            });
        } catch (error) {
            console.warn('Error adding marker event listeners:', error);
        }

        this.markers.push({ name: point.name, marker });
    }

    private debouncedUpdate(coords: MapCoord, resized: boolean, mode: 'preview' | 'final' = 'final') {
        if (mode === 'preview') {
            const coordsKey = `${coords.north.toFixed(6)},${coords.east.toFixed(6)},${coords.south.toFixed(6)},${coords.west.toFixed(6)}`;
            if (coordsKey === this.lastPreviewCoords) return;
            this.lastPreviewCoords = coordsKey;

            // Preview smooth : Max 60fps
            if (this.previewRafId !== null)
                cancelAnimationFrame(this.previewRafId);

            this.previewRafId = requestAnimationFrame(() => {
                if (this.onUpdate) this.onUpdate(coords, resized, 'preview');
                this.previewRafId = null;
            });

            return;
        }

        if (this.updateTimeout) clearTimeout(this.updateTimeout);

        this.updateTimeout = setTimeout(() => {
            if (this.onUpdate) this.onUpdate(coords, resized, 'final');
            this.updateTimeout = null;
        }, ImageTransform.FINAL_DEBOUNCE_DELAY);
    }

    addCenterMarker() {
        const centerLat = (this.resizedCoord.north + this.resizedCoord.south) / 2;
        const centerLng = (this.resizedCoord.east + this.resizedCoord.west) / 2;

        const svgMarkerCenter = {
            path: ImageTransform.CENTER_MARKER_SVG,
            fillColor: "white",
            fillOpacity: 1,
            strokeColor: "white",
            strokeWeight: 1,
            scale: 0.5,
            anchor: ImageTransform.CENTER_ANCHOR,
        };

        let centerMarker = new google.maps.Marker({
            position: { lat: centerLat, lng: centerLng },
            icon: svgMarkerCenter,
            map: this.map.googleMap
        });

        centerMarker.setDraggable(true);

        google.maps.event.addListener(centerMarker, 'dragstart', () => {
            this.dragStartTime = Date.now();
            this.isDragging = true;
        });

        google.maps.event.addListener(centerMarker, 'drag', () => {
            if (!this.isDragging) return;

            const now = Date.now();
            if (now - this.dragStartTime < 0) return;

            requestAnimationFrame(() => {
                this.handleCenterMarkerDrag();
                this.debouncedUpdate(this.resizedCoord, this.resized, 'preview');
            });
        });

        google.maps.event.addListener(centerMarker, 'dragend', () => {
            this.isDragging = false;
            this.resized = true;
            this.handleCenterMarkerDrag("dragend");

            this.debouncedUpdate(
                {
                    north: this.resizedCoord.north,
                    east: this.resizedCoord.east,
                    south: this.resizedCoord.south,
                    west: this.resizedCoord.west,
                },
                this.resized,
                'final'
            );
        });


        this.markers.push({ name: "center", marker: centerMarker });
    }

    handleCenterMarkerDrag(event: "drag" | "dragend" = "drag") {
        const centerMarker = this.markers.find(m => m.name === "center")?.marker;
        if (!centerMarker) return;

        const newCenterPosition = centerMarker.getPosition();
        if (!newCenterPosition) return;

        const latOffset = newCenterPosition.lat() - (this.tempCoord.north + this.tempCoord.south) / 2;
        const lngOffset = newCenterPosition.lng() - (this.tempCoord.east + this.tempCoord.west) / 2;

        this.tempCoord = {
            north: this.tempCoord.north + latOffset,
            east: this.tempCoord.east + lngOffset,
            south: this.tempCoord.south + latOffset,
            west: this.tempCoord.west + lngOffset,
        };

        this.resizedCoord = { ...this.tempCoord };

        this.points.forEach(p => {
            switch (p.name) {
                case "N":
                    p.lat = this.resizedCoord.north;
                    p.lng = this.resizedCoord.west;
                    break;
                case "E":
                    p.lat = this.resizedCoord.north;
                    p.lng = this.resizedCoord.east;
                    break;
                case "S":
                    p.lat = this.resizedCoord.south;
                    p.lng = this.resizedCoord.east;
                    break;
                case "W":
                    p.lat = this.resizedCoord.south;
                    p.lng = this.resizedCoord.west;
                    break;
            }
        });

        this.updateMarkerPosition("N", { lat: this.resizedCoord.north, lng: this.resizedCoord.west });
        this.updateMarkerPosition("E", { lat: this.resizedCoord.north, lng: this.resizedCoord.east });
        this.updateMarkerPosition("S", { lat: this.resizedCoord.south, lng: this.resizedCoord.east });
        this.updateMarkerPosition("W", { lat: this.resizedCoord.south, lng: this.resizedCoord.west });

        if (event === "dragend") {
            this.updateCenterMarkerPosition();
        }
    }

    updateMarkerPosition(markerName: string, newPosition: { lat: number; lng: number }) {
        const targetMarker = this.markers.find(m => m.name === markerName);
        if (targetMarker) {
            targetMarker.marker.setPosition(newPosition);
        }
    }

    updateCenterMarkerPosition() {
        const centerLat = (this.resizedCoord.north + this.resizedCoord.south) / 2;
        const centerLng = (this.resizedCoord.east + this.resizedCoord.west) / 2;

        const centerMarker = this.markers.find(marker => marker.name === "center")?.marker;

        if (centerMarker) {
            centerMarker.setPosition({ lat: centerLat, lng: centerLng });
        }
    }

    updateMarkers(movedMarker: string) {
        this.updateCoord(movedMarker);
        this.syncMarkersToCoords();
    }

    private syncMarkersToCoords() {
        const positions = {
            N: { lat: this.resizedCoord.north, lng: this.resizedCoord.west },
            E: { lat: this.resizedCoord.north, lng: this.resizedCoord.east },
            S: { lat: this.resizedCoord.south, lng: this.resizedCoord.east },
            W: { lat: this.resizedCoord.south, lng: this.resizedCoord.west }
        };

        Object.entries(positions).forEach(([name, pos]) => {
            this.updateMarkerPosition(name, pos);
        });
    }

    updateCoord(point: string) {
        let N = this.points.find(p => p.name === "N");
        let E = this.points.find(p => p.name === "E");
        let S = this.points.find(p => p.name === "S");
        let W = this.points.find(p => p.name === "W");

        if (!N || !E || !S || !W) {
            console.error("Missing points for coordinate update");
            return;
        }

        switch (point) {
            case "N": // Coin nord-ouest
                this.resizedCoord.north = N.lat;
                this.resizedCoord.west = N.lng;
                break;
            case "E": // Coin nord-est
                this.resizedCoord.north = E.lat;
                this.resizedCoord.east = E.lng;
                break;
            case "S": // Coin sud-est
                this.resizedCoord.south = S.lat;
                this.resizedCoord.east = S.lng;
                break;
            case "W": // Coin sud-ouest
                this.resizedCoord.south = W.lat;
                this.resizedCoord.west = W.lng;
                break;
            case "center":
                this.resizedCoord = {
                    north: Math.max(N.lat, E.lat),
                    east: Math.max(E.lng, S.lng),
                    south: Math.min(S.lat, W.lat),
                    west: Math.min(N.lng, W.lng)
                };
                break;
            default:
                break;
        }

        this.tempCoord = { ...this.resizedCoord };
        this.updateCenterMarkerPosition();
        if (!this.validateCoordinates()) {
            console.warn("Image coordinates may be invalid after transformation");
        }
    }

    private validateCoordinates(): boolean {
        const { north, south, east, west } = this.resizedCoord;

        if (north <= south) {
            console.warn(`Invalid latitude: north (${north}) <= south (${south})`);
            return false;
        }

        if (east <= west) {
            console.warn(`Invalid longitude: east (${east}) <= west (${west})`);
            return false;
        }

        return true;
    }
}
